import { useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import EventIcon from '@mui/icons-material/Event';
import AssignmentIcon from '@mui/icons-material/Assignment';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import dayjs from 'dayjs';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import { useQuery, useQueries } from '@tanstack/react-query';
import { fetchUsers } from '../../api/admin';
import { fetchAllStudents } from '../../api/students';
import { fetchTutorSchedules } from '../../api/schedules';
import { ROLES } from '../../utils/roles';
import { getWeekStartDate } from '../../utils/week';
import PeriodSelector from '../../components/admin/PeriodSelector';

const B = { primary: '#4357AD', dark: '#1E2A4A' };
const cardSx = { borderRadius: 4, boxShadow: '0 2px 12px rgba(67,87,173,0.08)', bgcolor: '#fff' };
const avatarColors = ['#4357AD', '#22C55E', '#F59E0B', '#EC4899', '#8B5CF6', '#06B6D4', '#EF4444', '#14B8A6'];
const pct = (a, b) => (b > 0 ? Math.round((a / b) * 100) : 0);

const StatBox = ({ label, value, color, icon }) => (
  <Box sx={{ textAlign: 'center', flex: 1 }}>
    <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5} sx={{ mb: 0.25 }}>
      {icon}
      <Typography variant="h6" fontWeight={800} sx={{ color, lineHeight: 1 }}>{value}</Typography>
    </Stack>
    <Typography variant="caption" sx={{ color: '#8891A5' }}>{label}</Typography>
  </Box>
);

const AdminReportsPage = () => {
  const defaultStart = getWeekStartDate(dayjs()).format('YYYY-MM-DD');
  const defaultEnd = dayjs(defaultStart).add(6, 'day').format('YYYY-MM-DD');

  const [periodStart, setPeriodStart] = useState(defaultStart);
  const [periodEnd, setPeriodEnd] = useState(defaultEnd);
  const [periodLabel, setPeriodLabel] = useState('Поточний тиждень');

  const handlePeriodChange = (start, end, label) => {
    setPeriodStart(start);
    setPeriodEnd(end);
    setPeriodLabel(label);
  };

  const usersQuery = useQuery({ queryKey: ['users'], queryFn: fetchUsers });
  const studentsQuery = useQuery({ queryKey: ['students'], queryFn: fetchAllStudents });
  const tutors = useMemo(() => (usersQuery.data || []).filter((u) => u.role === ROLES.TUTOR), [usersQuery.data]);

  // Calculate weeks to fetch
  const weeks = useMemo(() => {
    const result = [];
    let w = getWeekStartDate(dayjs(periodStart));
    const end = dayjs(periodEnd);
    while (w.isBefore(end) || w.isSame(end, 'day')) {
      result.push(w.format('YYYY-MM-DD'));
      w = w.add(1, 'week');
    }
    return result;
  }, [periodStart, periodEnd]);

  const scheduleQueries = useQueries({
    queries: tutors.flatMap((tutor) =>
      weeks.map((week) => ({
        queryKey: ['schedule', tutor.id, week],
        queryFn: () => fetchTutorSchedules(tutor.id, week),
        enabled: Boolean(tutor.id),
        staleTime: 60000,
      }))
    ),
  });

  // Build per-tutor event arrays filtered by period
  const tutorEvents = useMemo(() => {
    const map = {};
    const start = dayjs(periodStart);
    const end = dayjs(periodEnd).endOf('day');
    tutors.forEach((tutor, tIdx) => {
      const events = [];
      weeks.forEach((_, wIdx) => {
        const qIdx = tIdx * weeks.length + wIdx;
        (scheduleQueries[qIdx]?.data || []).forEach((e) => {
          const d = dayjs(e.startTime);
          if ((d.isAfter(start) || d.isSame(start, 'day')) && (d.isBefore(end) || d.isSame(end, 'day'))) {
            events.push(e);
          }
        });
      });
      map[tutor.id] = events;
    });
    return map;
  }, [tutors, weeks, scheduleQueries, periodStart, periodEnd]);

  const studentsByTutor = useMemo(() => {
    const map = {};
    (studentsQuery.data || []).forEach((s) => { if (s.tutorId) { if (!map[s.tutorId]) map[s.tutorId] = []; map[s.tutorId].push(s); } });
    return map;
  }, [studentsQuery.data]);

  const isLoading = usersQuery.isLoading || studentsQuery.isLoading;
  const allLoaded = scheduleQueries.every((q) => !q.isLoading);

  const allEvents = useMemo(() => Object.values(tutorEvents).flat(), [tutorEvents]);

  const globalStats = useMemo(() => {
    let planned = 0, completed = 0, cancelled = 0, rescheduled = 0;
    allEvents.forEach((e) => { planned++; if (e.status === 'COMPLETED') completed++; if (e.status === 'CANCELLED') cancelled++; if (e.status === 'RESCHEDULED') rescheduled++; });
    const totalStudents = (studentsQuery.data || []).length;
    const incompleteStudents = (studentsQuery.data || []).filter((s) => (s.incompleteLessons || 0) > 0).length;
    return { planned, completed, cancelled, rescheduled, totalStudents, incompleteStudents };
  }, [allEvents, studentsQuery.data]);

  // Chart: group by day across entire period
  const chartData = useMemo(() => {
    const dayMap = {};
    allEvents.forEach((e) => {
      const day = dayjs(e.startTime).format('DD.MM');
      if (!dayMap[day]) dayMap[day] = { day, Проведено: 0, Заплановано: 0, Перенесено: 0, Скасовано: 0 };
      if (e.status === 'COMPLETED') dayMap[day]['Проведено']++;
      else if (e.status === 'SCHEDULED') dayMap[day]['Заплановано']++;
      else if (e.status === 'RESCHEDULED') dayMap[day]['Перенесено']++;
      else if (e.status === 'CANCELLED') dayMap[day]['Скасовано']++;
    });
    return Object.values(dayMap).sort((a, b) => {
      const [dA, mA] = a.day.split('.').map(Number);
      const [dB, mB] = b.day.split('.').map(Number);
      return mA !== mB ? mA - mB : dA - dB;
    });
  }, [allEvents]);

  return (
    <Stack spacing={2.5}>
      <Card sx={{ ...cardSx, background: `linear-gradient(135deg, ${B.dark} 0%, ${B.primary} 100%)` }}>
        <CardContent sx={{ py: 3, px: 3.5 }}>
          <Typography variant="h4" fontWeight={800} sx={{ color: '#fff' }}>Звіт: репетитори</Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 0.5 }}>
            {periodLabel} ({dayjs(periodStart).format('DD.MM.YYYY')} — {dayjs(periodEnd).format('DD.MM.YYYY')})
          </Typography>
        </CardContent>
      </Card>

      <PeriodSelector mode="week" periodStart={periodStart} periodEnd={periodEnd} onChange={handlePeriodChange} />

      {!allLoaded && (
        <Box sx={{ px: 2, py: 1, bgcolor: '#FFFBEB', borderRadius: 3, border: '1px solid #FDE68A' }}>
          <Typography variant="caption" sx={{ color: '#92400E' }}>Завантаження даних...</Typography>
        </Box>
      )}

      <Card sx={cardSx}>
        <CardContent sx={{ py: 2.5 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: B.dark, mb: 2 }}>Загальна статистика</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' }, gap: 2 }}>
            <StatBox label="Заплановано" value={globalStats.planned} color={B.primary} icon={<EventIcon sx={{ fontSize: 16, color: B.primary }} />} />
            <StatBox label="Проведено" value={globalStats.completed} color="#22C55E" icon={<CheckCircleIcon sx={{ fontSize: 16, color: '#22C55E' }} />} />
            <StatBox label="Скасовано" value={globalStats.cancelled} color="#EF4444" icon={<CancelIcon sx={{ fontSize: 16, color: '#EF4444' }} />} />
            <StatBox label="Перенесено" value={globalStats.rescheduled} color="#F59E0B" icon={<SwapHorizIcon sx={{ fontSize: 16, color: '#F59E0B' }} />} />
            <StatBox label="Учнів" value={globalStats.totalStudents} color={B.primary} icon={<AssignmentIcon sx={{ fontSize: 16, color: B.primary }} />} />
            <StatBox label="Незаповнені" value={globalStats.incompleteStudents} color={globalStats.incompleteStudents > 0 ? '#EF4444' : '#22C55E'} icon={<WarningAmberIcon sx={{ fontSize: 16, color: globalStats.incompleteStudents > 0 ? '#EF4444' : '#22C55E' }} />} />
          </Box>
        </CardContent>
      </Card>

      {chartData.length > 0 && (
        <Card sx={cardSx}>
          <CardContent sx={{ py: 2.5 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: B.dark, mb: 2 }}>Активність за період</Typography>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData} barGap={2} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#8891A5' }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#8891A5' }} axisLine={false} tickLine={false} width={28} />
                <RechartsTooltip contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }} />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                <Bar dataKey="Проведено" fill="#22C55E" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Заплановано" fill={B.primary} radius={[4, 4, 0, 0]} />
                <Bar dataKey="Перенесено" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Скасовано" fill="#EF4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: B.primary }} /></Box>
      ) : (
        <Stack spacing={2}>
          <Typography variant="h6" fontWeight={700} sx={{ color: B.dark }}>По репетиторах</Typography>
          {tutors.map((tutor, idx) => {
            const events = tutorEvents[tutor.id] || [];
            const students = studentsByTutor[tutor.id] || [];
            const color = avatarColors[idx % avatarColors.length];
            const planned = events.length;
            const completed = events.filter((e) => e.status === 'COMPLETED').length;
            const cancelled = events.filter((e) => e.status === 'CANCELLED').length;
            const rescheduled = events.filter((e) => e.status === 'RESCHEDULED').length;
            const scheduled = events.filter((e) => e.status === 'SCHEDULED').length;
            const cancelPct = pct(cancelled, planned);
            const reschedulePct = pct(rescheduled, planned);
            const completionPct = pct(completed, planned - cancelled);
            const incompleteStudents = students.filter((s) => (s.incompleteLessons || 0) > 0);
            const totalIncomplete = incompleteStudents.reduce((sum, s) => sum + (s.incompleteLessons || 0), 0);
            const allJournalsFilled = incompleteStudents.length === 0;

            // Per-tutor chart by day
            const tutorChart = (() => {
              const dm = {};
              events.forEach((e) => {
                const d = dayjs(e.startTime).format('DD.MM');
                if (!dm[d]) dm[d] = { day: d, Проведено: 0, Заплановано: 0, Перенесено: 0, Скасовано: 0 };
                if (e.status === 'COMPLETED') dm[d]['Проведено']++;
                else if (e.status === 'SCHEDULED') dm[d]['Заплановано']++;
                else if (e.status === 'RESCHEDULED') dm[d]['Перенесено']++;
                else if (e.status === 'CANCELLED') dm[d]['Скасовано']++;
              });
              return Object.values(dm).sort((a, b) => { const [dA, mA] = a.day.split('.').map(Number); const [dB, mB] = b.day.split('.').map(Number); return mA !== mB ? mA - mB : dA - dB; });
            })();

            return (
              <Card key={tutor.id} sx={{ ...cardSx, border: '1.5px solid #EDE9FE' }}>
                <CardContent sx={{ py: 2.5, px: 3 }}>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <Avatar sx={{ width: 44, height: 44, bgcolor: `${color}15`, color, fontWeight: 700, fontSize: 17, border: `2px solid ${color}30` }}>
                      {tutor.name?.charAt(0)?.toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ color: B.dark }}>{tutor.name}</Typography>
                      <Typography variant="caption" sx={{ color: '#8891A5' }}>{students.length} учнів</Typography>
                    </Box>
                    <Chip label={`${completionPct}% проведених`} size="small" sx={{ fontWeight: 700, fontSize: 12, bgcolor: completionPct >= 80 ? '#D1FAE5' : completionPct >= 50 ? '#FEF3C7' : '#FEE2E2', color: completionPct >= 80 ? '#166534' : completionPct >= 50 ? '#92400E' : '#991B1B' }} />
                  </Stack>

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    {[
                      { v: planned, l: 'Заплановано', bg: '#EDE9FE', c: B.primary },
                      { v: completed, l: 'Проведено', bg: '#D1FAE5', c: '#22C55E' },
                      { v: cancelled, l: `Скасовано (${cancelPct}%)`, bg: '#FEE2E2', c: '#EF4444' },
                      { v: rescheduled, l: `Перенесено (${reschedulePct}%)`, bg: '#FEF3C7', c: '#F59E0B' },
                    ].map((s) => (
                      <Grid item xs={6} sm={3} key={s.l}>
                        <Box sx={{ px: 2, py: 1.5, borderRadius: 3, bgcolor: s.bg, textAlign: 'center' }}>
                          <Typography variant="h5" fontWeight={800} sx={{ color: s.c }}>{s.v}</Typography>
                          <Typography variant="caption" sx={{ color: '#8891A5' }}>{s.l}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  {tutorChart.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={tutorChart} barGap={2} barCategoryGap="20%">
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                          <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#8891A5' }} axisLine={false} tickLine={false} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#8891A5' }} axisLine={false} tickLine={false} width={20} />
                          <RechartsTooltip contentStyle={{ borderRadius: 10, border: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.08)', fontSize: 12 }} />
                          <Bar dataKey="Проведено" fill="#22C55E" radius={[3, 3, 0, 0]} />
                          <Bar dataKey="Заплановано" fill={B.primary} radius={[3, 3, 0, 0]} />
                          <Bar dataKey="Перенесено" fill="#F59E0B" radius={[3, 3, 0, 0]} />
                          <Bar dataKey="Скасовано" fill="#EF4444" radius={[3, 3, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  )}

                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                    <TrendingUpIcon sx={{ fontSize: 18, color: B.primary }} />
                    <Box sx={{ flex: 1 }}>
                      <LinearProgress variant="determinate" value={completionPct} sx={{ height: 8, borderRadius: 4, bgcolor: '#EDE9FE', '& .MuiLinearProgress-bar': { borderRadius: 4, background: `linear-gradient(90deg, ${B.primary}, #22C55E)` } }} />
                    </Box>
                    <Typography variant="caption" fontWeight={700} sx={{ color: B.primary, minWidth: 35 }}>{completionPct}%</Typography>
                  </Stack>

                  <Box sx={{ px: 2, py: 1.5, borderRadius: 3, bgcolor: allJournalsFilled ? '#F0FDF4' : '#FFFBEB', border: `1px solid ${allJournalsFilled ? '#BBF7D0' : '#FDE68A'}` }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <AssignmentIcon sx={{ fontSize: 18, color: allJournalsFilled ? '#22C55E' : '#F59E0B' }} />
                      <Typography variant="body2" fontWeight={600} sx={{ color: allJournalsFilled ? '#166534' : '#92400E', flex: 1 }}>
                        {allJournalsFilled ? 'Всі журнали заповнено' : `${totalIncomplete} незаповнен${totalIncomplete === 1 ? 'ий урок' : 'их уроків'} у ${incompleteStudents.length} учн${incompleteStudents.length === 1 ? 'я' : 'ів'}`}
                      </Typography>
                      {!allJournalsFilled && (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {incompleteStudents.slice(0, 5).map((s) => <Chip key={s.id} label={`${s.name} (${s.incompleteLessons})`} size="small" sx={{ bgcolor: '#FEF3C7', color: '#92400E', fontWeight: 600, fontSize: 10, height: 22 }} />)}
                          {incompleteStudents.length > 5 && <Chip label={`+${incompleteStudents.length - 5}`} size="small" sx={{ bgcolor: '#FDE68A', color: '#92400E', fontWeight: 600, fontSize: 10, height: 22 }} />}
                        </Stack>
                      )}
                    </Stack>
                  </Box>
                  {scheduled > 0 && <Typography variant="caption" sx={{ color: '#8891A5', mt: 1, display: 'block' }}>Ще {scheduled} занять заплановано</Typography>}
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};

export default AdminReportsPage;
